import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { X, ChevronDown } from 'lucide-react';
import TouchGestures from './TouchGestures';

export interface MobileModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnSwipeDown?: boolean;
  fullScreen?: boolean;
  maxHeight?: string;
  className?: string;
  headerActions?: React.ReactNode;
  footer?: React.ReactNode;
  preventScroll?: boolean;
}

const MobileModal: React.FC<MobileModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  subtitle,
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnSwipeDown = true,
  fullScreen = false,
  maxHeight = '90vh',
  className,
  headerActions,
  footer,
  preventScroll = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const startTouchY = useRef<number>(0);
  const currentTouchY = useRef<number>(0);
  const isDragging = useRef<boolean>(false);

  // Check if mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle modal opening/closing animations
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Prevent body scroll when modal is open
      if (preventScroll) {
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = 'var(--scrollbar-width, 0px)';
      }
    } else {
      // Delay hiding to allow close animation
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);

      // Restore body scroll
      if (preventScroll) {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }

      return () => clearTimeout(timer);
    }
  }, [isOpen, preventScroll]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle swipe down to close
  const handleSwipeDown = () => {
    if (closeOnSwipeDown) {
      onClose();
    }
  };

  // Check if content is scrolled to top for swipe-to-close
  const isScrolledToTop = () => {
    if (!contentRef.current) return true;
    return contentRef.current.scrollTop === 0;
  };

  if (!isVisible) return null;

  const modalContent = (
    <div
      ref={modalRef}
      className={cn(
        'fixed inset-0 z-50 flex items-end justify-center',
        isMobile ? 'mobile-modal-backdrop' : 'bg-black/50 items-center'
      )}
      onClick={handleBackdropClick}
    >
      <TouchGestures
        onSwipeDown={closeOnSwipeDown && isScrolledToTop() ? handleSwipeDown : undefined}
        threshold={100}
        className="w-full max-w-2xl"
      >
        <div
          className={cn(
            'relative bg-background shadow-xl transition-all duration-300',
            isMobile && !fullScreen && 'mobile-modal-content rounded-t-xl',
            isMobile && fullScreen && 'fixed inset-0 rounded-none',
            !isMobile && 'rounded-lg max-w-2xl mx-4',
            isOpen && isMobile && 'open',
            className
          )}
          style={{
            maxHeight: isMobile && !fullScreen ? maxHeight : undefined,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag handle for mobile */}
          {isMobile && closeOnSwipeDown && !fullScreen && (
            <div className="flex justify-center pt-2 pb-1">
              <div className="w-8 h-1 bg-muted-foreground/30 rounded-full" />
            </div>
          )}

          {/* Header */}
          {(title || subtitle || showCloseButton || headerActions) && (
            <div className={cn(
              'flex items-center justify-between p-4 border-b border-border',
              isMobile && 'safe-area-top'
            )}>
              <div className="flex-1 min-w-0">
                {title && (
                  <h2 className="mobile-heading truncate">
                    {title}
                  </h2>
                )}
                {subtitle && (
                  <p className="mobile-caption text-muted-foreground truncate">
                    {subtitle}
                  </p>
                )}
              </div>
              
              <div className="flex items-center space-x-2 ml-4">
                {headerActions}
                {showCloseButton && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="touch-target"
                    aria-label="Close modal"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Content */}
          <div
            ref={contentRef}
            className={cn(
              'overflow-y-auto',
              isMobile && fullScreen && 'flex-1',
              !fullScreen && 'max-h-[calc(90vh-8rem)]'
            )}
            style={{
              maxHeight: isMobile && !fullScreen ? `calc(${maxHeight} - 8rem)` : undefined,
            }}
          >
            <div className={cn(
              'p-4',
              isMobile && 'mobile-padding'
            )}>
              {children}
            </div>
          </div>

          {/* Footer */}
          {footer && (
            <div className={cn(
              'border-t border-border p-4',
              isMobile && 'safe-area-bottom'
            )}>
              {footer}
            </div>
          )}
        </div>
      </TouchGestures>
    </div>
  );

  return createPortal(modalContent, document.body);
};

// Hook for managing modal state with mobile optimizations
export const useMobileModal = (initialOpen = false) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [isClosing, setIsClosing] = useState(false);

  const open = () => {
    setIsOpen(true);
    setIsClosing(false);
  };

  const close = () => {
    setIsClosing(true);
    // Delay actual close to allow animation
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 300);
  };

  return {
    isOpen,
    isClosing,
    open,
    close,
    toggle: () => (isOpen ? close() : open()),
  };
};

// Component for bottom sheet style modals
export const BottomSheet: React.FC<Omit<MobileModalProps, 'fullScreen'>> = (props) => {
  return <MobileModal {...props} fullScreen={false} />;
};

// Component for full screen modals
export const FullScreenModal: React.FC<Omit<MobileModalProps, 'fullScreen'>> = (props) => {
  return <MobileModal {...props} fullScreen={true} />;
};

// Higher-order component for making any modal mobile-responsive
export const withMobileModal = <P extends object>(
  Component: React.ComponentType<P>
) => {
  return React.forwardRef<any, P & MobileModalProps>((props, ref) => {
    const { isOpen, onClose, title, subtitle, ...componentProps } = props;
    
    return (
      <MobileModal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        subtitle={subtitle}
      >
        <Component {...(componentProps as P)} ref={ref} />
      </MobileModal>
    );
  });
};

export default MobileModal;